import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"

export type SliderTimer = {
  total: number
  current: number
  running: boolean
  start: () => void
  pause: () => void
  goNext: () => void
  goPrev: () => void
  goTo: (index: number) => void
}

export function useSlideTimer(args: {
  /**
   * The total number of slides
   */
  total: number
  /**
   * Slide duration, in seconds
   * @default 4
   **/
  slideDuration?: number
  /**
   * Whether the timer is enabled. Useful for keeping the timer asleep when the element is not visible.
   *
   * @default true
   */
  enabled?: boolean
  /**
   * Allows you to hook into slide changes directly. You can usually just use the `current` value from the returned object.
   */
  onSlideChange?: (index: number) => void
}): SliderTimer {
  const [running, setRunning] = useState(true)
  const [_current, setCurrent] = useState(0)
  const current = _current % args.total

  const onSlideChange = useRef(args.onSlideChange!)
  onSlideChange.current = args.onSlideChange!

  const goTo = (index: number) => {
    setCurrent(index % args.total)
    if (onSlideChange.current) onSlideChange.current(index % args.total)
  }

  useEffect(() => {
    if (!running || args.enabled === false) return
    const timer = setTimeout(
      () => {
        goTo((current + 1) % args.total)
      },
      (args.slideDuration ?? 4) * 1000,
    )
    return () => clearTimeout(timer)
  }, [running, current, args.enabled, args.slideDuration, args.total])

  return {
    total: args.total,
    current,
    running,
    start: () => setRunning(true),
    pause: () => setRunning(false),
    goNext: () => goTo((current + 1) % args.total),
    goPrev: () => goTo((current - 1 + args.total) % args.total),
    goTo: (index: number) => goTo(index),
  }
}

const SliderTimerContext = createContext<SliderTimer | null>(null)

export const SliderTimerProvider = SliderTimerContext.Provider

export function useParentSlideTimer() {
  return useContext(SliderTimerContext)
}
